/**
 * CoverClubContext — global state for Cover Club.
 *
 * Only the user's own interactions persist locally (reactions, votes,
 * comments they've written, reports, custom collections); the base
 * CoverEntry/battle data is the seed catalog. Displayed reaction counts are
 * derived as (seed count + 1 if this user's own reaction is active) rather
 * than mutating the seed counts directly, so toggling never double-counts.
 */

import React, { createContext, useContext, useCallback, useMemo, useState } from 'react';
import { useLocalStore } from '@/lib/useLocalStore';
import { useAuth } from '@/lib/useAuth';
import { useBookshelf } from '@/components/bookshelf';
import { COVER_ENTRIES, BATTLES } from '../constants/seedCoverClub';
import { getBookById, toShelfBook } from '../utils/bookCatalog';

const CoverClubContext = createContext(undefined);

const makeId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export function CoverClubProvider({ children }) {
  const { getCoverClubFeed, getCoverClubBattles, voteCoverBattle, getCoverClubCollections, createCoverCollection, addCoverCollectionItem, deleteCoverCollectionItem } = useAuth();
  const { books: shelfBooks, addBook, moveBook } = useBookshelf();

  const [myReactions, setMyReactions] = useLocalStore('sr-cover-club-reactions', {});
  const [myVotes, setMyVotes] = useLocalStore('sr-cover-club-votes', {});
  const [myComments, setMyComments] = useLocalStore('sr-cover-club-comments', {});
  const [reportedIds, setReportedIds] = useLocalStore('sr-cover-club-reported', []);
  const [customCollections, setCustomCollections] = useLocalStore('sr-cover-club-collections', []);
  const [feedEntries, setFeedEntries] = useState(null);
  const [apiBattles, setApiBattles] = useState(null);
  const [apiCollections, setApiCollections] = useState(null);

  const loadFeed = useCallback(async () => {
    const feedPayload = await getCoverClubFeed();
    const feed = feedPayload?.data?.entries
      || feedPayload?.data?.items
      || feedPayload?.data
      || feedPayload?.entries
      || feedPayload?.items;
    if (Array.isArray(feed)) setFeedEntries(feed);
    return feed;
  }, [getCoverClubFeed]);

  const loadBattles = useCallback(async () => {
    const payload = await getCoverClubBattles();
    const battles = payload?.data?.battles
      || payload?.data?.items
      || payload?.data
      || payload?.battles
      || payload?.items;
    if (Array.isArray(battles)) setApiBattles(battles);
    return battles;
  }, [getCoverClubBattles]);

  const loadCollections = useCallback(async () => {
    const payload = await getCoverClubCollections();
    const collections = payload?.data?.collections
      || payload?.data?.items
      || payload?.data
      || payload?.collections
      || payload?.items;
    if (Array.isArray(collections)) setApiCollections(collections);
    return collections;
  }, [getCoverClubCollections]);

  const entries = useMemo(
    () => (feedEntries ?? COVER_ENTRIES)
      .map((entry) => ({ ...entry, book: getBookById(entry.bookId) }))
      .filter((entry) => entry.book),
    [feedEntries]
  );

  const getEntry = useCallback(
    (bookId) => entries.find((e) => e.bookId === bookId) ?? null,
    [entries]
  );

  const getReactionCounts = useCallback((entry) => {
    const mine = myReactions[entry.bookId];
    const counts = { ...entry.reactionCounts };
    if (mine) counts[mine] = (counts[mine] ?? 0) + 1;
    return counts;
  }, [myReactions]);

  const toggleReaction = useCallback((bookId, reactionKey) => {
    setMyReactions((prev) => ({
      ...prev,
      [bookId]: prev[bookId] === reactionKey ? null : reactionKey,
    }));
  }, [setMyReactions]);

  const getComments = useCallback((bookId) => {
    const entry = COVER_ENTRIES.find((e) => e.bookId === bookId);
    const seedComments = entry?.comments ?? [];
    const mine = myComments[bookId] ?? [];
    return [...seedComments, ...mine]
      .filter((c) => !reportedIds.includes(c.id))
      .sort((a, b) => a.createdAt - b.createdAt);
  }, [myComments, reportedIds]);

  const addComment = useCallback((bookId, text) => {
    const comment = { id: makeId('comment'), author: 'you', text: text.trim(), createdAt: Date.now(), mine: true };
    setMyComments((prev) => ({ ...prev, [bookId]: [...(prev[bookId] ?? []), comment] }));
    return comment;
  }, [setMyComments]);

  const reportComment = useCallback((commentId) => {
    setReportedIds((prev) => (prev.includes(commentId) ? prev : [...prev, commentId]));
  }, [setReportedIds]);

  const getBattleResults = useCallback((battle) => {
    const myVote = myVotes[battle.id];
    const votes = { ...battle.votes };
    if (myVote) votes[myVote] = (votes[myVote] ?? 0) + 1;
    const total = votes.a + votes.b;
    return {
      myVote: myVote ?? null,
      votes,
      total,
      pctA: total ? Math.round((votes.a / total) * 100) : 50,
      pctB: total ? Math.round((votes.b / total) * 100) : 50,
    };
  }, [myVotes]);

  const voteBattle = useCallback(async (battleId, choice) => {
    // One vote per battle per user — prevents duplicate/changed votes.
    if (myVotes[battleId]) return;
    await voteCoverBattle(battleId, choice);
    setMyVotes((prev) => ({ ...prev, [battleId]: choice }));
  }, [myVotes, setMyVotes, voteCoverBattle]);

  const isBookOnShelf = useCallback(
    (bookId) => shelfBooks.some((b) => b.id === bookId),
    [shelfBooks]
  );

  const addToShelf = useCallback((bookId, shelfId) => {
    const alreadyShelved = shelfBooks.find((b) => b.id === bookId);
    if (alreadyShelved) {
      return moveBook(bookId, shelfId);
    }
    const book = getBookById(bookId);
    if (!book) return Promise.resolve();
    return addBook(toShelfBook(book, shelfId), shelfId);
  }, [shelfBooks, addBook, moveBook]);

  const createCollection = useCallback(async ({ title, description, bookIds }) => {
    const payload = await createCoverCollection({ title: title?.trim() || 'Untitled Collection', description: description?.trim() ?? '' });
    const result = payload?.data?.collection || payload?.data || payload?.collection || payload;
    const collection = { ...result, id: result.id ?? result.collectionId, title: result.title ?? title, description: result.description ?? description, bookIds: result.bookIds ?? bookIds ?? [], curator: result.curator ?? 'you', custom: true };
    for (const bookId of bookIds ?? []) await addCoverCollectionItem(collection.id, bookId);
    setCustomCollections((prev) => [collection, ...prev]);
    return collection;
  }, [addCoverCollectionItem, createCoverCollection, setCustomCollections]);

  const removeCollectionItem = useCallback(async (collectionId, bookId) => {
    await deleteCoverCollectionItem(collectionId, bookId);
    setCustomCollections((previous) => previous.map((collection) => collection.id === collectionId ? { ...collection, bookIds: collection.bookIds.filter((id) => id !== bookId) } : collection));
  }, [deleteCoverCollectionItem, setCustomCollections]);

  const value = useMemo(() => ({
    entries,
    loadFeed,
    loadBattles,
    loadCollections,
    getEntry,
    getReactionCounts,
    toggleReaction,
    myReactions,
    getComments,
    addComment,
    reportComment,
    battles: apiBattles ?? BATTLES,
    getBattleResults,
    voteBattle,
    isBookOnShelf,
    addToShelf,
    customCollections: apiCollections ?? customCollections,
    createCollection,
    removeCollectionItem,
  }), [
    entries, loadFeed, loadBattles, loadCollections, getEntry, getReactionCounts, toggleReaction, myReactions, getComments,
    addComment, reportComment, getBattleResults, voteBattle, isBookOnShelf, addToShelf,
    apiBattles, apiCollections, customCollections, createCollection, removeCollectionItem,
  ]);

  return <CoverClubContext.Provider value={value}>{children}</CoverClubContext.Provider>;
}

export function useCoverClub() {
  const context = useContext(CoverClubContext);
  if (!context) {
    throw new Error('useCoverClub must be used within CoverClubProvider');
  }
  return context;
}
